import "./listing.scss";

import React from "react";
import { useTranslation } from "react-i18next";

import { AddItemButton } from "@sbi-international/boutik.gatsby.plugin.checkout";
import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import {
    ProductPrice,
    ProductStock,
    Rating,
} from "@sbi-international/boutik.gatsby.plugin.components";
import droletArrowRight from "../../../src/images/icons/primaryDroletArrowRight.svg";
import GatsbyImageWrapper from "../GatsbyImageWrapper";
import { IFilterOptions } from "../../interfaces";
import { getAvailability } from "../../utils";
import { AllProductsNodeModel } from "../../models/allProductsNodeModel";
import { ArchiveProductModel } from "../../models/archiveProductModel";
import { icons } from "../../constants/icons";
import downloadIcon from "../../images/icons/download-icon.svg";

export default function Listing(props: {
    title?: string;
    items: AllProductsNodeModel[] | ArchiveProductModel[];
    displayLink: boolean;
    hasOwnPage: boolean;
    displayManual: boolean;
    displayRatings?: boolean;
    filterOptions?: IFilterOptions;
    displayDescription?: boolean;
    displayPreOrder?: boolean;
}) {
    const { t } = useTranslation("layout");
    const { currentLocale, navigateTranslated } = usePageLocalization();

    const {
        title,
        items,
        displayLink,
        hasOwnPage,
        displayManual,
        filterOptions,
        displayRatings,
        displayDescription,
        displayPreOrder,
    } = props;

    return (
        <>
            <div className="drolet-alist-container">
                {title && <div className="alist-title">{title}</div>}
                {items &&
                    items
                        ?.filter((item) => item.static !== undefined)
                        .map((item: AllProductsNodeModel | ArchiveProductModel, index: number) => (
                            <Single
                                key={item.static.code}
                                item={item}
                                index={index}
                                displayLink={displayLink}
                                hasOwnPage={hasOwnPage}
                                displayManual={displayManual}
                                displayRatings={displayRatings}
                                filterOptions={filterOptions}
                                displayDescription={true}
                            />
                        ))}
            </div>
        </>
    );

    function Single(props: {
        item: AllProductsNodeModel | ArchiveProductModel;
        index: number;
        displayLink?: boolean;
        hasOwnPage: boolean;
        displayManual?: boolean;
        displayRatings?: boolean;
        filterOptions?: IFilterOptions;
        displayDescription?: boolean;
    }) {
        const { item, displayRatings, displayDescription } = props;

        return (
            <div className="alist-single">                
                <div
                    className={hasOwnPage ? "clickable-zone hasLink" : "clickable-zone"}
                    onClick={() =>
                        hasOwnPage &&
                        item?.static?.slug?.[currentLocale] &&
                        navigateTranslated(item.static?.slug[currentLocale])
                    }
                >
                    <div className="alist-image">
                        <GatsbyImageWrapper
                            fields={item?.static.fields ?? undefined}
                            className="list-img"
                        />
                    </div>
                    <div className="alist-info">
                        <span className="alist-sku">{item?.static.code}</span>
                        <span className="alist-info-title">
                            {item?.static.title?.[currentLocale]}
                        </span>

                        {item?.static.description && displayDescription && (
                            <div
                                className="alist-description"
                                dangerouslySetInnerHTML={{
                                    __html:
                                        item?.static.description?.valuesMarkdownRemark?.[
                                            currentLocale
                                        ] ?? "",
                                }}
                            />
                        )}

                        {displayManual && item?.static.documents?.[0]?.[currentLocale] && (
                            <a
                                rel="noreferrer"
                                target="_blank"
                                href={"/file/" + item?.static.documents[0]?.[currentLocale]}
                                className="document"
                            >
                                <span className="alist-download">
                                    {t("appliance.download-manual")}
                                    <img src={downloadIcon} alt=""></img>
                                </span>
                            </a>
                        )}

                        {displayRatings &&
                            !!item.productReviews?.count &&
                            item.productReviews?.count > 0 && (
                                <div className="alist-reviews">
                                    <Rating.RatingScore
                                        ratingScore={item.productReviews?.average ?? 0}
                                        ratingCount={item.productReviews?.count ?? undefined}
                                    />
                                </div>
                            )}
                    </div>
                </div>
                <div className="mobile-cart-group">
                    <div className="mobile-price-group">
                        <div className="alist-price-avail">
                            <ProductPrice
                                currentPrice={item?.live?.currentPrice}
                                prefixClassName="listing-single-product-single"
                            />
                            <ProductStock
                                currentStock={item?.live?.currentStock}
                                isCustomMade={false}
                                type="small"
                            />
                        </div>
                    </div>
                    <div className="alist-add-to-cart">
                        <AddItemButton
                            type={"medium"}
                            product={item.live}                            
                            pluginCheckoutIconsModel={icons.pluginCheckoutIconsMedium}                            
                        />

                        {displayLink && (
                            <span
                                className="alist-see-more"
                                onClick={() => {
                                    navigateTranslated(item?.static.slug?.[currentLocale] ?? "");
                                }}
                            >
                                {t("appliance.see-more")}
                                <img
                                    className={`alist-iconRight`}
                                    src={droletArrowRight}
                                    alt={""}
                                />
                            </span>
                        )}
                    </div>
                </div>
                {displayLink && (
                    <span
                        className="alist-see-more-small"
                        style={{ display: "none" }}
                        onClick={() => {
                            navigateTranslated(item?.static.slug?.[currentLocale] ?? "");
                        }}
                    >
                        {t("appliance.see-more")}
                        <img className={`alist-iconRight`} src={droletArrowRight} alt={""} />
                    </span>
                )}
            </div>
        );
    }
}
